import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  BookOpen,
  Star,
  Clock,
  Tag,
  Filter,
} from "lucide-react";
import { Card } from "../components/UI";
import { PageLoader, ButtonLoader } from "../components/LoadingSpinner";
import { ErrorAlert, InlineError } from "../components/ErrorAlert";
import educationApi from "../api/education";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const RISK_TAGS = ["all", "low", "moderate", "high"];
const CATEGORY_MAP = {
  mental_health: "Kesehatan Mental",
  sleep_management: "Manajemen Tidur",
  physical_recovery: "Pemulihan Fisik",
  nutrition: "Nutrisi",
  baby_blues: "Baby Blues",
  social_support: "Dukungan Sosial",
};

// Kebalikannya untuk filter ke BE
const CATEGORY_MAP_REVERSE = Object.fromEntries(
  Object.entries(CATEGORY_MAP).map(([k, v]) => [v, k]),
);

const RISK_COLORS = {
  All: "bg-gray-100 text-gray-600 border-gray-200",
  Low: "bg-green-100 text-green-700 border-green-200",
  Moderate: "bg-yellow-100 text-yellow-700 border-yellow-200",
  High: "bg-red-100 text-red-700 border-red-200",
};
const CAT_COLORS = {
  "Kesehatan Mental": "bg-purple-100 text-purple-700",
  "Manajemen Tidur": "bg-indigo-100 text-indigo-700",
  "Pemulihan Fisik": "bg-green-100 text-green-700",
  Nutrisi: "bg-orange-100 text-orange-700",
  "Baby Blues": "bg-pink-100 text-pink-700",
  "Dukungan Sosial": "bg-blue-100 text-blue-700",
};

const EMPTY_FORM = {
  title: "",
  summary: "",
  content: "",
  category: "mental_health",
  risk_tags: [],
  read_minutes: 5,
  is_important: false,
};

export default function EducationPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mutateError, setMutateError] = useState("");
  const [saving, setSaving] = useState(false);

  const [filterCat, setFilterCat] = useState("Semua");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await educationApi.getAll();
      setArticles(res.data?.data ?? res.data ?? []);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Gagal memuat artikel",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // ── Open forms ────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      title: a.title,
      summary: a.summary || "",
      content: a.content || "",
      category: a.category,
      risk_tags: a.risk_tags ?? [],
      read_minutes: a.read_minutes ?? 5,
      is_important: a.is_important ?? false,
    });
    setShowForm(true);
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const save = async () => {
    if (!form.title.trim()) return;
    console.log("form dikirim:", JSON.stringify(form));
    setSaving(true);
    setMutateError("");
    try {
      if (editingId) {
        const res = await educationApi.update(editingId, form);
        const updated = res.data?.data ?? res.data;
        setArticles((prev) =>
          prev.map((a) => (a.id === editingId ? updated : a)),
        );
      } else {
        const res = await educationApi.create(form);
        const created = res.data?.data ?? res.data;
        setArticles((prev) => [...prev, created]);
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
    } catch (err) {
      setMutateError(err.response?.data?.message || "Gagal menyimpan artikel");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const del = async (id) => {
    if (!window.confirm("Hapus artikel ini?")) return;
    setMutateError("");
    try {
      await educationApi.remove(id);
      setArticles((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      setMutateError(err.response?.data?.message || "Gagal menghapus artikel");
    }
  };

  const filtered = articles.filter(
    (a) =>
      filterCat === "Semua" || a.category === CATEGORY_MAP_REVERSE[filterCat],
  );

  if (loading)
    return (
      <div className="p-6">
        <PageLoader message="Memuat artikel edukasi..." />
      </div>
    );
  if (error)
    return (
      <div className="p-6">
        <ErrorAlert message={error} onRetry={fetchArticles} />
      </div>
    );

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="font-display font-800 text-2xl text-gray-900">
            Manajemen Edukasi Kesehatan
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {articles.length} artikel tersedia untuk ibu nifas
          </p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-800 hover:bg-primary-900 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" /> Tambah Artikel
        </button>
      </div>

      <InlineError message={mutateError} />

      {/* Modal Tambah / Edit */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => {
              setShowForm(false);
              setEditingId(null);
            }}
          />

          {/* Modal card */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
              <div>
                <h3 className="font-display font-700 text-gray-900">
                  {editingId ? "Edit Artikel" : "Tambah Artikel Baru"}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {editingId
                    ? "Perbarui informasi artikel"
                    : "Isi form untuk menambah artikel baru"}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Judul Artikel <span className="text-red-400">*</span>
                  </label>
                  <input
                    value={form.title}
                    onChange={(e) => setField("title", e.target.value)}
                    placeholder="Judul artikel edukasi..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Ringkasan
                  </label>
                  <textarea
                    value={form.summary}
                    onChange={(e) => setField("summary", e.target.value)}
                    placeholder="Ringkasan singkat..."
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Konten Artikel
                  </label>
                  <div data-color-mode="light">
                    <ReactQuill
                      theme="snow"
                      value={form.content}
                      onChange={(val) => setField("content", val)}
                      style={{ height: "200px", marginBottom: "42px" }}
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Kategori
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setField("category", e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                  >
                    {Object.entries(CATEGORY_MAP).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Tag Risiko
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {RISK_TAGS.map((r) => (
                      <label
                        key={r}
                        className="flex items-center gap-2 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={form.risk_tags.includes(r)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setField("risk_tags", [...form.risk_tags, r]);
                            } else {
                              setField(
                                "risk_tags",
                                form.risk_tags.filter((t) => t !== r),
                              );
                            }
                          }}
                          className="w-4 h-4 rounded accent-primary-800"
                        />
                        <span
                          className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${RISK_COLORS[r.charAt(0).toUpperCase() + r.slice(1)] || RISK_COLORS.All}`}
                        >
                          {r.charAt(0).toUpperCase() + r.slice(1)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Menit Baca
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={60}
                    value={form.read_minutes}
                    onChange={(e) => setField("read_minutes", +e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <div
                      onClick={() =>
                        setField("is_important", !form.is_important)
                      }
                      className={`w-10 h-6 rounded-full transition-colors relative cursor-pointer ${form.is_important ? "bg-primary-800" : "bg-gray-300"}`}
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.is_important ? "translate-x-5" : "translate-x-1"}`}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      Tandai sebagai Penting
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-2 p-5 border-t border-gray-100 sticky bottom-0 bg-white rounded-b-2xl">
              <button
                onClick={save}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary-800 text-white text-sm font-semibold rounded-xl hover:bg-primary-900 disabled:opacity-60 transition-colors"
              >
                {saving ? (
                  <>
                    <ButtonLoader /> Menyimpan...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />{" "}
                    {editingId ? "Simpan Perubahan" : "Tambah Artikel"}
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
                disabled={saving}
                className="px-5 py-2.5 bg-gray-100 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-200 disabled:opacity-60 transition-colors"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
        {["Semua", ...Object.values(CATEGORY_MAP)].map((c) => (
          <button
            key={c}
            onClick={() => setFilterCat(c)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
              ${filterCat === c ? "bg-primary-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">
                  Judul
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5 hidden md:table-cell">
                  Kategori
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5 hidden lg:table-cell">
                  Tag Risiko
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5 hidden lg:table-cell">
                  Baca
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3.5">
                  Penting
                </th>
                <th className="px-5 py-3.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((a) => (
                <tr
                  key={a.id}
                  className={`hover:bg-gray-50 transition-colors ${editingId === a.id ? "bg-primary-50" : ""}`}
                >
                  <td className="px-5 py-4 max-w-xs">
                    <div className="flex items-start gap-2">
                      <BookOpen className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-gray-900 leading-snug">
                          {a.title}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2 hidden sm:block">
                          {a.summary}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <span
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CAT_COLORS[CATEGORY_MAP[a.category]] || "bg-gray-100 text-gray-600"}`}
                    >
                      {CATEGORY_MAP[a.category] ?? a.category}
                    </span>
                  </td>
                  <td className="px-5 py-4 hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(a.risk_tags ?? []).map((tag) => (
                        <span
                          key={tag}
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${RISK_COLORS[tag.charAt(0).toUpperCase() + tag.slice(1)] || RISK_COLORS.All}`}
                        >
                          {tag.charAt(0).toUpperCase() + tag.slice(1)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden lg:table-cell">
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="w-3.5 h-3.5" />{" "}
                      {a.read_minutes ?? a.readMin} menit
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    {(a.is_important ?? a.important) ? (
                      <Star className="w-4 h-4 text-accent-600 fill-accent-500" />
                    ) : (
                      <Star className="w-4 h-4 text-gray-200" />
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit(a)}
                        className="p-2 text-gray-400 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => del(a.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-5 py-12 text-center text-sm text-gray-400"
                  >
                    Tidak ada artikel pada kategori ini
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
